import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, Check, Heart, Activity, Brain, Home, 
  Calendar, Wallet, Users, Star, ChevronDown, ChevronUp,
  BookOpen, Target, Dumbbell, Moon, Utensils, TrendingUp
} from 'lucide-react';

const LIVON_LOGO = "https://static.prod-images.emergentagent.com/jobs/b293bd06-5db6-47e3-9bac-b35b794055ae/images/24e9f4cae61d982d9c454efb36258018ae47a7c7ef1d43817c3543666c110b4a.png";

// Features data
const FEATURES = [
  {
    icon: Heart,
    title: 'Mina tankar & känslor',
    description: 'Dagbok, humörloggning, tacksamhet och självreflektion',
    color: 'bg-rose-100 text-rose-600'
  },
  {
    icon: Activity,
    title: 'Min hälsa',
    description: 'Träningsprogram, kostplanering, sömn och hormonspårning',
    color: 'bg-emerald-100 text-emerald-600'
  },
  {
    icon: Brain,
    title: 'Mitt lugn',
    description: 'Meditation, andningsövningar och fokusverktyg',
    color: 'bg-purple-100 text-purple-600'
  },
  {
    icon: Home,
    title: 'Mitt hem',
    description: 'Städscheman, inköpslistor och hemprojekt',
    color: 'bg-amber-100 text-amber-600'
  },
  {
    icon: Calendar,
    title: 'Min planering',
    description: 'Mål, vanor, påminnelser och veckoplanering',
    color: 'bg-blue-100 text-blue-600'
  },
  {
    icon: Wallet,
    title: 'Min ekonomi',
    description: 'Budget, sparande, skulder och ekonomikoll',
    color: 'bg-teal-100 text-teal-600'
  },
  {
    icon: Users,
    title: 'Mina relationer',
    description: 'Kommunikation, gränser och relationsvård',
    color: 'bg-pink-100 text-pink-600'
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    name: 'Anna K.',
    text: 'Livon har hjälpt mig få struktur på min vardag. Äntligen en app som förstår att livet är mer än bara träning eller jobb!',
    rating: 5
  },
  {
    name: 'Marcus L.',
    text: 'Perfekt för oss som vill ha allt på ett ställe. Slipper 10 olika appar för att hålla koll på livet.',
    rating: 5
  },
  {
    name: 'Sofia B.',
    text: 'Träningsprogrammen och humörloggningen är fantastiska. Ser verkligen min utveckling över tid.',
    rating: 5
  }
];

// FAQ data
const FAQ = [
  {
    question: 'Vad kostar Livon?',
    answer: 'Livon kostar 299 kr – en engångsbetalning som ger dig full åtkomst till alla funktioner. Ingen månadsavgift. Du får 3 dagars gratis provperiod först.'
  },
  {
    question: 'Fungerar det på min mobil?',
    answer: 'Ja! Livon fungerar på alla enheter – mobil, surfplatta och dator. Du kan även installera appen på din hemskärm för snabb åtkomst.'
  },
  {
    question: 'Kan jag testa innan jag betalar?',
    answer: 'Absolut! Du får 3 dagars gratis provperiod med tillgång till alla funktioner. Ingen betalning krävs förrän provperioden är slut.'
  },
  {
    question: 'Hur säkra är mina uppgifter?',
    answer: 'Vi tar din integritet på allvar. All data är krypterad, vi säljer aldrig dina uppgifter, och du kan radera ditt konto när som helst.'
  },
  {
    question: 'Vem driver Livon?',
    answer: 'Livon drivs av Expressa AB (org.nr 556940-4436), ett svenskt företag. Alla köp och avtal ingås med Expressa AB.'
  }
];

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex justify-between items-center text-left"
      >
        <span className="font-medium text-gray-900">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 text-gray-600">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function MarketingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md z-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src={LIVON_LOGO} alt="Livon" className="h-10 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/auth')}
              className="hidden sm:inline-flex"
            >
              Logga in
            </Button>
            <Button 
              onClick={() => navigate('/auth')}
              className="btn-warm rounded-full"
            >
              Prova gratis
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-6">
            <Star className="w-4 h-4 fill-current" />
            3 dagars gratis provperiod
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Ditt liv i <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-500">en app</span>
          </h1>
          
          <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Livon hjälper dig få struktur, rutiner och bättre välmående i vardagen. 
            Dagbok, träning, ekonomi, relationer – allt på ett ställe.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              onClick={() => navigate('/auth')}
              className="btn-warm rounded-full px-8 py-6 text-lg group"
            >
              Kom igång gratis
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
          
          <p className="text-sm text-gray-500">
            299 kr engångsbetalning • Ingen månadsavgift • 3 dagars gratis provperiod
          </p>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-600">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-emerald-400 border-2 border-white" />
                ))}
              </div>
              <span className="font-medium">100+ användare</span>
            </div>
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
              ))}
              <span className="ml-2 font-medium">4.8 / 5 betyg</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Allt du behöver för ett balanserat liv
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Sju kategorier som täcker alla delar av livet – från hälsa och ekonomi till relationer och personlig utveckling.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, index) => (
              <div 
                key={index}
                className="p-6 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300 bg-white"
              >
                <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Highlight Features */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Dumbbell className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Träningsprogram</h3>
              <p className="text-gray-600">Professionella program för alla nivåer med illustrerade övningar</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Dagbok & reflektion</h3>
              <p className="text-gray-600">Skriv ner tankar, följ humör och upptäck mönster över tid</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Mål & vanor</h3>
              <p className="text-gray-600">Sätt upp mål, bygg vanor och följ din utveckling</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Vad användarna säger
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((testimonial, index) => (
              <div 
                key={index}
                className="p-6 rounded-2xl bg-white border border-gray-100 shadow-sm"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"{testimonial.text}"</p>
                <p className="font-medium text-gray-900">{testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Enkel och transparent prissättning
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            En plan. Alla funktioner. Betala en gång – ingen månadsavgift.
          </p>
          
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-4">
              3 dagar gratis
            </div>
            
            <div className="flex items-baseline justify-center gap-2 mb-2">
              <span className="text-5xl font-bold text-gray-900">299</span>
              <span className="text-xl text-gray-600">kr</span>
            </div>
            <p className="text-gray-500 mb-6">engångsbetalning • ingen månadsavgift</p>
            
            <Button 
              onClick={() => navigate('/auth')}
              className="w-full btn-warm rounded-full py-6 text-lg"
            >
              Starta din gratis provperiod
            </Button>
            
            <p className="text-sm text-gray-500 mt-4">
              Säker betalning via Stripe. Inga dolda avgifter.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Vanliga frågor
          </h2>
          
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            {FAQ.map((item, index) => (
              <FAQItem key={index} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-teal-500 to-emerald-500">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Redo att ta kontroll över ditt liv?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Börja din 3 dagars gratis provperiod idag.
          </p>
          <Button 
            onClick={() => navigate('/auth')}
            className="bg-white text-teal-600 hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold"
          >
            Kom igång nu – det är gratis
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900 text-gray-400">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img src={LIVON_LOGO} alt="Livon" className="h-8 w-auto brightness-0 invert" />
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors">Integritetspolicy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Användarvillkor</Link>
              <Link to="/cookies" className="hover:text-white transition-colors">Cookie-policy</Link>
              <a href="mailto:support@livon.se" className="hover:text-white transition-colors">Kontakt</a>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm">
            <p>© {new Date().getFullYear()} Livon®. En tjänst från Expressa AB (org.nr 556940-4436).</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
